import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import { DashboardPageShell } from '../components/dashboard';
import DashboardAccountMenu from '../components/dashboard/DashboardAccountMenu';
import { api } from '../api/http';
import { useAuth } from '../auth/AuthProvider';
import './BillingPage.css';

const { Text, Title } = Typography;

const PORTAL_STATUSES = new Set(['active', 'trialing', 'past_due', 'unpaid', 'incomplete']);

export function isSubscriptionManageable(subscription) {
  const status = String(subscription?.status || '').toLowerCase();
  return Boolean(subscription?.entitled) || PORTAL_STATUSES.has(status);
}

function hasPaymentIssue(subscription) {
  const status = String(subscription?.status || '').toLowerCase();
  return status === 'past_due' || status === 'unpaid' || status === 'incomplete';
}

function redirectToSessionUrl(data) {
  if (!data?.url) throw new Error('Billing session did not include a redirect URL.');
  window.location.assign(data.url);
}

export function BillingContent({ showHeading = true }) {
  const { session } = useAuth();
  const role = String(session?.role || '').toLowerCase();
  const [subscription, setSubscription] = useState({ status: 'loading', entitled: false, accessState: 'none' });
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [error, setError] = useState('');
  const [ownerRequired, setOwnerRequired] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const canViewBillingStatus = role === 'customer';
  const canManageBilling = session?.canManageBilling === true && !ownerRequired;

  const loadSubscription = useCallback(async () => {
    setLoadingSubscription(true);
    setError('');
    try {
      const { data } = await api.get('/api/billing/subscription');
      setSubscription({
        status: data?.status ?? 'none',
        entitled: Boolean(data?.entitled),
        accessState: data?.accessState ?? 'none',
        billableQuantity: Number(data?.billableQuantity ?? 0),
      });
      setSubscriptionLoaded(true);
    } catch (err) {
      setSubscription({ status: 'error', entitled: false, accessState: 'none' });
      setSubscriptionLoaded(false);
      setError(err?.message || 'Unable to load billing status. Please try again.');
    } finally {
      setLoadingSubscription(false);
    }
  }, []);

  useEffect(() => {
    if (canViewBillingStatus) loadSubscription();
  }, [canViewBillingStatus, loadSubscription]);

  const hasManageableSubscription = useMemo(
    () => isSubscriptionManageable(subscription),
    [subscription]
  );
  const showPaymentIssueGuidance = useMemo(() => hasPaymentIssue(subscription), [subscription]);

  const startCheckout = async (plan) => {
    if (!canManageBilling || pendingAction) return;
    setError('');
    if (!termsAccepted) {
      setError('Agree to the Customer Agreement before starting Checkout.');
      return;
    }
    setPendingAction(plan);
    try {
      await api.post('/api/legal/terms-acceptance', {});
      const { data } = await api.post(`/api/billing/checkout-session?plan=${plan}`);
      redirectToSessionUrl(data);
    } catch (err) {
      if (err?.data?.code === 'BILLING_OWNER_REQUIRED') {
        setOwnerRequired(true);
        setError('');
        return;
      }
      if (err?.data?.code === 'TERMS_ACCEPTANCE_REQUIRED') {
        setTermsAccepted(false);
        setError('Accept the current Customer Agreement before starting Checkout.');
        return;
      }
      setError(err?.message || 'Unable to start Stripe Checkout. Please try again.');
    } finally {
      setPendingAction('');
    }
  };

  const openPortal = async () => {
    if (!canManageBilling || pendingAction) return;
    setPendingAction('portal');
    setError('');
    try {
      const { data } = await api.get('/api/billing/portal');
      redirectToSessionUrl(data);
    } catch (err) {
      if (err?.data?.code === 'BILLING_OWNER_REQUIRED') {
        setOwnerRequired(true);
        setError('');
        return;
      }
      setError(err?.message || 'Unable to open the billing portal. Please try again.');
    } finally {
      setPendingAction('');
    }
  };

  const ownerManagedBilling = (
    <div className="billing-page-actions">
      <Alert
        className="billing-page-alert"
        showIcon
        type="info"
        message="Billing is managed by the Account Owner."
        description="Only the Account Owner can start or change subscriptions, update payment information, or resolve billing issues."
      />
      <Text>Current subscription status: {String(subscription?.status || 'none')}.</Text>
    </div>
  );

  let body;
  if (!canViewBillingStatus) {
    body = <Alert showIcon type="error" message="Billing is available only to customer administrators." />;
  } else if (loadingSubscription) {
    body = <Spin><div className="billing-page-loading">Loading billing status…</div></Spin>;
  } else if (!subscriptionLoaded) {
    body = (
      <div className="billing-page-actions">
        <Text>Billing choices are unavailable until subscription status loads successfully.</Text>
        <Button onClick={loadSubscription}>Retry</Button>
      </div>
    );
  } else if (!canManageBilling) {
    body = ownerManagedBilling;
  } else if (hasManageableSubscription) {
    body = (
      <div className="billing-page-actions billing-manage-actions">
        {showPaymentIssueGuidance ? (
          <Alert
            className="billing-page-alert"
            showIcon
            type="warning"
            message="Payment information needs attention"
            description="Use the Stripe customer portal to update your payment method or resolve the subscription issue."
          />
        ) : null}
        <Button
          type="primary"
          className="dashboard-primary-action-button fleet-add-button"
          icon={<CreditCardOutlined />}
          loading={pendingAction === 'portal'}
          disabled={Boolean(pendingAction)}
          onClick={openPortal}
          aria-label="Manage subscription in Stripe customer portal"
        >
          Manage Subscription
        </Button>
      </div>
    );
  } else {
    const quantity = subscription.billableQuantity;
    const annual = selectedPlan === 'yearly';
    const unitPrice = annual ? 59.99 : 5.99;
    const recurring = (quantity * unitPrice).toFixed(2);
    body = (
      <div className="billing-page-actions" aria-label="Billing plan choices">
        <div className="billing-plan-selector">
          <Button type={!annual?'primary':'default'} onClick={()=>setSelectedPlan('monthly')}>Monthly</Button>
          <Button type={annual?'primary':'default'} onClick={()=>setSelectedPlan('yearly')}>Annual</Button>
        </div>
        <section className="billing-plan-option" aria-live="polite">
          <Title level={3}>{annual ? 'Annual' : 'Monthly'} subscription</Title>
          <Text className="billing-plan-price">{annual ? '$59.99 per forklift per year' : '$5.99 per active non-retired forklift per month'}</Text>
          <Text>Current Checkout quantity: {quantity} forklift{quantity === 1 ? '' : 's'}.</Text>
          <Text>Recurring {annual ? 'annual' : 'monthly'} amount: ${recurring}.</Text>
          <Text>A 90-day trial automatically transitions to paid billing unless canceled before the trial ends. The subscription then renews {annual ? 'annually' : 'monthly'}.</Text>
          <Text>Cancel through the Stripe Customer Portal. Cancellation ordinarily takes effect at the end of the applicable period. Payments are generally non-refundable, with no automatic prorated refund or credit for unused time except where required by law or otherwise approved.</Text>
          <div className="billing-terms-acceptance"><input id="billingTerms" type="checkbox" checked={termsAccepted} onChange={e=>setTermsAccepted(e.target.checked)}/><label htmlFor="billingTerms">I agree to the <a href="/terms" target="_blank" rel="noreferrer">Customer Agreement</a> and acknowledge the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Notice</a>.</label></div>
          <Button type="primary" className="dashboard-primary-action-button" loading={pendingAction===selectedPlan} disabled={Boolean(pendingAction)} onClick={()=>startCheckout(selectedPlan)} aria-label={`Start ${annual?'annual':'monthly'} Stripe Checkout`}>Start {annual?'Annual':'Monthly'} Checkout</Button>
        </section>
      </div>
    );
  }

  return (
    <section
      className="billing-content"
      aria-labelledby={showHeading ? 'billing-page-title' : undefined}
      aria-label={showHeading ? undefined : 'Billing subscription management'}
    >
      {showHeading && <div className="billing-page-heading">
        <Title level={2} id="billing-page-title">Billing</Title>
        <Text type="secondary">Choose a plan or manage your existing ManageLift subscription using Stripe.</Text>
      </div>}
      {error ? <Alert className="billing-page-alert" showIcon type="error" message="Billing action failed" description={error} /> : null}
      {body}
    </section>
  );
}

const headerActions = ({ activeCustomerName, logout, navigate, session }) => (
  <DashboardAccountMenu
    label="Account"
    logout={logout}
    navigate={navigate}
    session={session}
    customerName={activeCustomerName}
    overlayClassName="customer-preview-account-dropdown"
    className="customer-preview-account-menu"
  />
);

export default function BillingPage() {
  return (
    <DashboardPageShell title="Billing" renderHeaderActions={headerActions} headerClassName="customer-preview-header" contentClassName="customer-preview-page" shellClassName="billing-page-shell">
      <section className="billing-page-panel">
        <BillingContent />
      </section>
    </DashboardPageShell>
  );
}
